import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PublicPageNav from '../components/PublicPageNav';
import { useAuth } from '../contexts/AuthContext';
import ZipSuggestions from '../components/common/ZipSuggestions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  'Valet Trash Door Step Collection',
  'Recycling Collection Programs',
  'Janitorial and Sanitation',
  'Bulk Item Removal',
  'Overflow Waste Support',
  'Dumpster Treatment',
  'Day Porter Services and Property Appearance Patrol',
  'Pressure Washing',
  'Window Cleaning',
  'Floor Care (Floor Polishing, Floor Restoration)',
  'Carpet Cleaning and Extraction',
  'Deep Cleaning',
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const HEAR_ABOUT_OPTIONS = [
  'Social Media',
  'Property Management',
  'NECS Employee',
  'Friend / Word of Mouth',
  'Online Search (Google, etc.)',
  'Event / Trade Show',
  'Existing Client Referral',
  'Print / Flyer',
  'Other',
];

const SOURCE_HEADINGS = {
  pricing: { tag: 'Pricing' },
  demo:    { tag: 'Demo' },
  contact: { tag: 'Contact' },
};

export default function ContactPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const source = params.get('source') || 'contact';
  const presetService = params.get('service') || '';
  const presetProperty = params.get('property_name') || '';
  const presetPackage = params.get('current_package') || '';
  // Iter-98 Homepage · Two-line packages CTA prefill. When the visitor
  // clicks "Get a Quote" on a General Commercial tier card we route to
  // /contact?type=<property_type>&tier=<basic|standard|premium>&service_line=<NECS Line>.
  const presetPropertyType = params.get('type') || '';
  const presetTier = params.get('tier') || '';
  const presetServiceLine = params.get('service_line') || '';
  // SOURCE_HEADINGS retained for future per-source UI variations; unified title is "Email Us"
  void SOURCE_HEADINGS;

  // Where to send the user "back" — PM dashboards for PMs, admin for
  // admins, staff dashboard for staff, tenants to their portal, and
  // everyone else to the public homepage.
  const returnTo = user
    ? user.role === 'property_manager' ? { path: '/property-manager-dashboard', label: 'Property Manager Dashboard' }
    : user.role === 'company_manager' ? { path: '/company-manager-dashboard', label: 'NECS Admin Dashboard' }
    : (user.role === 'admin' || user.role === 'supervisor' || user.role === 'crew_leader' || user.role === 'agent')
        ? { path: '/staff-dashboard', label: 'Staff Dashboard' }
    : user.role === 'tenant' ? { path: '/tenant-dashboard', label: 'Resident Portal' }
    : { path: '/', label: 'Home' }
    : { path: '/', label: 'Home' };

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    company_or_property: presetProperty || user?.property_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    street_address: '', address_line_2: '',
    city: '', state: 'TX', zip_code: '',
    services: SERVICES.includes(presetService) ? [presetService] : [],
    preferred_starting_date: '',
    contact_preference: 'email',
    hear_about: '',
    // Iter-92 · Default the "I am a…" dropdown to Current NECS Client so
    // existing clients feel recognized immediately. Signed-in users keep
    // the value that matches their actual role.
    client_type: user?.role === 'property_manager' ? 'property_manager'
      : user?.role === 'tenant' ? 'resident'
      : 'current_client',
    client_role: user?.role === 'property_manager' ? 'property_manager' : '',
    // Pre-fill upgrade prompt into the message so PM doesn't retype.
    message: source === 'upgrade' && presetPackage
      ? `Requesting an upgrade from my current ${presetPackage}. Please share the next tier options and any onboarding steps.`
      : (presetServiceLine || presetTier || presetPropertyType)
        ? `Requesting a quote for ${presetServiceLine || 'a NECS commercial service line'}${presetTier ? ` (${presetTier.charAt(0).toUpperCase() + presetTier.slice(1)} tier)` : ''}${presetPropertyType ? ` — property type: ${presetPropertyType}` : ''}. Please share available options for our property and next steps to finalize a custom quote.`
        : '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const toggleService = (svc) => {
    setForm((s) => {
      const next = s.services.includes(svc)
        ? s.services.filter((x) => x !== svc)
        : [...s.services, svc];
      return { ...s, services: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_type) {
      toast.error('Please pick a role from the "I am a…" dropdown.');
      return;
    }
    if (form.message.trim().length < 1) {
      toast.error('Please add a message so our team knows how to help.');
      return;
    }
    if (form.contact_preference === 'phone' && !form.phone.trim()) {
      toast.error('A phone number is required when you prefer phone contact.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Could not send. Please try again.');
      } else {
        setDone(true);
        toast.success('Message sent — we will respond within one business day.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundColor: '#2D2D2D',
        color: '#e5e5e5',
        backgroundImage: "url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/zlr6cctl_1564.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
      data-testid="contact-page"
    >
      {/* dark scrim so the form remains readable over the photograph */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 100%)' }}
        data-testid="contact-bg-scrim"
      />

      <div className="relative">
        <PublicPageNav testIdPrefix="contact" />

        <main className="max-w-3xl mx-auto px-6 pt-28 pb-12">
          {source === 'upgrade' && (
            <div
              className="mb-6 rounded-lg p-4 border-2"
              style={{ borderColor: '#FFD60A', backgroundColor: 'rgba(255,214,10,0.08)' }}
              data-testid="contact-upgrade-banner"
            >
              <p className="text-xs uppercase tracking-[0.28em] font-bold mb-1" style={{ color: '#FFD60A', fontFamily: 'Barlow Condensed, sans-serif' }}>
                · Upgrade Request
              </p>
              <p className="text-sm" style={{ color: '#f5f5f5' }}>
                {presetProperty && <><strong>{presetProperty}</strong> · </>}
                {presetPackage && <>current package: <strong>{presetPackage}</strong> · </>}
                A Noble Empire manager will contact you within one business day to walk through the next tier.
              </p>
            </div>
          )}

          {(presetServiceLine || presetPropertyType || presetTier) && source !== 'upgrade' && (
            <div
              className="mb-6 rounded-lg p-4 border-2"
              style={{ borderColor: '#C9A962', backgroundColor: 'rgba(201,169,98,0.08)' }}
              data-testid="contact-commercial-quote-banner"
            >
              <p className="text-xs uppercase tracking-[0.28em] font-bold mb-1" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}>
                · Commercial Quote Request
              </p>
              <p className="text-sm" style={{ color: '#f5f5f5' }}>
                {presetServiceLine && <><strong data-testid="contact-preset-service-line">{presetServiceLine}</strong> · </>}
                {presetTier && <>tier: <strong data-testid="contact-preset-tier">{presetTier.charAt(0).toUpperCase() + presetTier.slice(1)}</strong> · </>}
                {presetPropertyType && <>property type: <strong data-testid="contact-preset-type">{presetPropertyType.replace(/_/g, ' ')}</strong> · </>}
                Final pricing is confirmed by your NECS account manager after we review your property.
              </p>
            </div>
          )}

          <p
            className="text-base sm:text-lg text-center mb-2"
            style={{ color: '#f5f5f5', fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.18em' }}
            data-testid="contact-hero-eyebrow"
          >
            Contact Noble Empire Cleaning Services
          </p>
          <h1
            className="text-3xl sm:text-4xl font-black text-center text-white mb-3"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            data-testid="contact-hero-headline"
          >
            An Active Operating Company Serving Live Clients Nationwide
          </h1>
          <p
            className="text-base sm:text-lg text-center mb-10 max-w-2xl mx-auto"
            style={{ color: '#f5f5f5' }}
            data-testid="contact-hero-subcopy"
          >
            Existing clients, commercial prospects, property managers, and residents — tell us how to help and a Noble Empire specialist follows up within one business day.
          </p>

        {done ? (
          <div className="rounded-xl p-8 text-center border-2" style={{ borderColor: '#C9A962', backgroundColor: '#1f1f1f' }} data-testid="contact-success">
            <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Message Received</h2>
            <p className="text-sm" style={{ color: '#a0a0a0' }}>
              Thanks, {form.first_name || 'friend'}! Our team will reach out to <span className="text-[#C9A962]">{form.email}</span> within one business day.
            </p>
            {user ? (
              <Button
                type="button"
                className="mt-6 font-bold"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                onClick={() => navigate(returnTo.path)}
                data-testid="contact-back-to-dashboard-success-btn"
              >
                ← Return to {returnTo.label}
              </Button>
            ) : (
              <Link to="/">
                <Button className="mt-6 font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} data-testid="contact-back-to-home-btn">
                  Back to Home
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 rounded-xl p-6 sm:p-8 border" style={{ borderColor: '#3D3D3D', backgroundColor: '#1f1f1f' }} data-testid="contact-form">

            {/* Back-to-dashboard chip anchored to the top-left INSIDE
                the form card. Keeps the NECS logo in the nav bar fully
                visible above and lets signed-in users return to their
                own dashboard with one tap. */}
            {user && (
              <div className="flex" data-testid="contact-back-to-dashboard-strip">
                <button
                  type="button"
                  onClick={() => navigate(returnTo.path)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 font-bold text-xs hover:opacity-90 transition"
                  style={{ borderColor: '#C9A962', color: '#C9A962', backgroundColor: 'rgba(201,169,98,0.08)' }}
                  data-testid="contact-back-to-dashboard-btn"
                >
                  ← Back to {returnTo.label}
                </button>
              </div>
            )}

            {/* Name row */}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="First Name *"><Input required value={form.first_name} onChange={update('first_name')} testId="inq-first-name" /></Field>
              <Field label="Last Name *"><Input required value={form.last_name} onChange={update('last_name')} testId="inq-last-name" /></Field>
            </div>

            <Field label="Company / Property Name *">
              <Input required value={form.company_or_property} onChange={update('company_or_property')} testId="inq-company" />
            </Field>

            {/* Client type — routes the inquiry so NECS can handle a
                current Valet Living client's upgrade differently from a
                cold prospective inquiry. */}
            {/* Iter-92 · "I am a…" dropdown per CEO directive. Replaces the
                older radio pair with a single select preset to "Current
                NECS Client" so existing clients feel recognized. Value
                still routed as `client_type` server-side. */}
            <div className="rounded-lg p-4 border" style={{ borderColor: '#C9A962', backgroundColor: '#171717' }} data-testid="inq-client-type-block">
              <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: '#C9A962' }}>I Am A… *</p>
              <select
                required
                value={form.client_type || 'current_client'}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((s) => ({
                    ...s,
                    client_type: v,
                    // For "current_client" default the role to property_manager
                    // (the most common current-client persona) unless the user
                    // is already signed in with a different role.
                    client_role: v === 'current_client'
                      ? (s.client_role || 'property_manager')
                      : v === 'property_manager' ? 'property_manager'
                      : v === 'resident' ? 'resident'
                      : '',
                  }));
                }}
                className="w-full px-3 py-2 rounded-md border bg-[#2D2D2D] text-white focus:outline-none focus:border-[#C9A962]"
                style={{ borderColor: '#3D3D3D' }}
                data-testid="inq-i-am-a"
              >
                <option value="prospective_commercial">Prospective Commercial Client</option>
                <option value="current_client">Current NECS Client</option>
                <option value="property_manager">Property Manager</option>
                <option value="resident">Resident</option>
                <option value="other">Other</option>
              </select>
              <p className="text-[10px] text-zinc-500 mt-2">
                Existing clients are triaged straight to their Customer Success owner.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email Address *"><Input required type="email" value={form.email} onChange={update('email')} testId="inq-email" /></Field>
              <Field label="Phone (required if Phone preferred below)"><Input type="tel" value={form.phone} onChange={update('phone')} testId="inq-phone" /></Field>
            </div>

            {/* Address */}
            <div className="rounded-lg p-4 border" style={{ borderColor: '#3D3D3D', backgroundColor: '#171717' }}>
              <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: '#C9A962' }}>Property Address</p>
              <div className="space-y-4">
                <Field label="Street Address *"><Input required value={form.street_address} onChange={update('street_address')} testId="inq-street" /></Field>
                <Field label="Address Line 2 (Apt, Suite, etc.)"><Input value={form.address_line_2} onChange={update('address_line_2')} testId="inq-line2" /></Field>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="City *"><Input required value={form.city} onChange={update('city')} testId="inq-city" /></Field>
                  <Field label="State *">
                    <select required value={form.state} onChange={update('state')} className="w-full px-3 py-2 rounded-md border bg-[#2D2D2D] text-white focus:outline-none focus:border-[#C9A962]" style={{ borderColor: '#3D3D3D' }} data-testid="inq-state">
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Zip Code *">
                    <Input required value={form.zip_code} onChange={update('zip_code')} testId="inq-zip" inputMode="numeric" />
                    <ZipSuggestions
                      city={form.city}
                      state={form.state}
                      currentZip={form.zip_code}
                      onPick={(z) => setForm((f) => ({ ...f, zip_code: z }))}
                      testid="inq-zip-suggestions"
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Nature of inquiry */}
            <Field label="Nature of Inquiry (select all that apply)">
              <div className="grid sm:grid-cols-2 gap-2">
                {SERVICES.map((svc) => {
                  const active = form.services.includes(svc);
                  return (
                    <button
                      key={svc}
                      type="button"
                      onClick={() => toggleService(svc)}
                      className={`text-left px-3 py-2 rounded-md text-sm border transition ${active ? 'border-[#C9A962] bg-[#C9A962]/15 text-white' : 'border-[#3D3D3D] bg-[#2D2D2D] text-zinc-300 hover:border-[#C9A962]/60'}`}
                      data-testid={`inq-service-${svc.split(' ')[0].toLowerCase()}`}
                      data-checked={active}
                    >
                      <span className="inline-block w-3 h-3 mr-2 rounded-sm align-middle" style={{ backgroundColor: active ? '#C9A962' : 'transparent', border: '1px solid #C9A962' }} />
                      {svc}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Master Addendum · Preferred Starting Date — tells NECS admin
                when the client wants service to begin. */}
            <Field label="Preferred Starting Date">
              <input
                type="date"
                value={form.preferred_starting_date}
                onChange={(e) => setForm((s) => ({ ...s, preferred_starting_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-md bg-[#2D2D2D] border border-[#3D3D3D] text-white focus:outline-none focus:border-[#C9A962]"
                data-testid="inq-preferred-start-date"
              />
            </Field>

            {/* Contact preference */}
            <Field label="Preferred Contact Method">
              <div className="flex gap-3" data-testid="inq-contact-pref">
                {['email', 'phone'].map((pref) => {
                  const active = form.contact_preference === pref;
                  return (
                    <button
                      key={pref}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, contact_preference: pref }))}
                      className={`flex-1 capitalize px-4 py-2 rounded-md text-sm font-semibold border transition ${active ? 'border-[#C9A962] bg-[#C9A962] text-[#2D2D2D]' : 'border-[#3D3D3D] bg-[#2D2D2D] text-zinc-300 hover:border-[#C9A962]/60'}`}
                      data-testid={`inq-pref-${pref}`}
                    >
                      {pref}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="How did you hear about us?">
              <select
                value={form.hear_about}
                onChange={update('hear_about')}
                className="w-full px-3 py-2 rounded-md border bg-[#2D2D2D] text-white focus:outline-none focus:border-[#C9A962]"
                style={{ borderColor: '#3D3D3D' }}
                data-testid="inq-hear-about"
              >
                <option value="">— Select an option —</option>
                {HEAR_ABOUT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </Field>

            <Field label="Message *">
              <textarea required minLength={1} maxLength={4000} rows={6} value={form.message} onChange={update('message')} placeholder="Tell us how we can help…" className="w-full px-3 py-2 rounded-md border bg-[#2D2D2D] text-white focus:outline-none focus:border-[#C9A962]" style={{ borderColor: '#3D3D3D' }} data-testid="inq-message" />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-xs" style={{ color: '#71717a' }}>
                By submitting you agree to our <Link to="/terms" className="text-[#C9A962] hover:underline">Terms</Link> and <Link to="/privacy" className="text-[#C9A962] hover:underline">Privacy Policy</Link>.
              </p>
              <Button type="submit" disabled={submitting} className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} data-testid="inq-submit">
                {submitting ? 'Sending…' : 'Send Inquiry →'}
              </Button>
            </div>
          </form>
        )}

        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm" data-testid="contact-direct-channels">
          <Channel label="Email" value="support@nobleempirecs.com" href="mailto:support@nobleempirecs.com" />
          <Channel label="Phone" value="+1 (888) 241-3077" href="tel:+18882413077" />
          <Channel label="Fax" value="(817) 766-9697" href="tel:+18177669697" />
          <Channel label="Office" value={'2450 Lakeside Pkwy, Ste 150\nFlower Mound, TX 75022'} />
        </div>

        {/* Iter-98 · Company service hours. Same block is mirrored in the
            website footer + Noble AI knowledge base so a visitor always
            knows when live phone support is available. */}
        <div
          className="mt-6 p-5 rounded-lg border"
          style={{ borderColor: 'rgba(201,169,98,0.4)', backgroundColor: 'rgba(201,169,98,0.06)' }}
          data-testid="contact-service-hours"
        >
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: '#C9A962' }}>
            Service Hours
          </p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-bold text-white mb-1" data-testid="contact-hours-phone-title">Phone Support</p>
              <p className="text-zinc-300">Monday through Friday · 8:30 AM to 4:30 PM CDT</p>
              <p className="text-[11px] text-zinc-500 mt-1">
                Outside these hours? Reach us instantly through the chat system.
              </p>
            </div>
            <div>
              <p className="font-bold text-white mb-1" data-testid="contact-hours-chat-title">Chat Support · 24/7</p>
              <p className="text-zinc-300">
                Noble AI answers anytime. A live Customer Service representative joins during business hours; outside those hours, escalations get a follow-up first thing the next business day.
              </p>
            </div>
          </div>
        </div>
      </main>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: '#C9A962' }}>{label}</span>
      {children}
    </label>
  );
}

function Input({ testId, ...rest }) {
  return (
    <input
      {...rest}
      className="w-full px-3 py-2 rounded-md border bg-[#2D2D2D] text-white focus:outline-none focus:border-[#C9A962]"
      style={{ borderColor: '#3D3D3D' }}
      data-testid={testId}
    />
  );
}

function Channel({ label, value, href }) {
  return (
    <div className="rounded-lg p-4 border" style={{ borderColor: '#3D3D3D', backgroundColor: '#1f1f1f' }}>
      <p className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: '#C9A962' }}>{label}</p>
      {href ? (
        <a href={href} className="text-sm text-white hover:text-[#C9A962] transition whitespace-pre-line">{value}</a>
      ) : (
        <p className="text-sm text-white whitespace-pre-line">{value}</p>
      )}
    </div>
  );
}
